<template>
	<div>
		<div class="stage">
			<page-header title="Flows" />

			<filter-bar
				:show-filters="showFilters"
				:show-add-button="$global.hasUserRole(userRoles, 'flows:create')"
				:add-button-label="display === 'flows' ? 'Add Flow' : 'Convert a Flow'"
				@toggle-filters="showFilters = !showFilters"
				@reset-filters="resetSearchParams"
				@add-new="display === 'flows' ? routeTo('/flows/new') : (showFlowSelectionModal = true)"
			>
				<template v-if="display === 'flows'" #extra-add-buttons>
					<div
						class="fabWrap group my-2 mr-8 cursor-default sm:my-0"
						@click="showTemplateSelectionModal = true"
					>
						<div class="fabLabel">
							<p class="whitespace-nowrap">Use Template</p>
						</div>
						<div
							class="fab h-10 min-h-10 w-10 min-w-10 bg-blue-500 group-hover:bg-blue-600 group-active:bg-blue-700 sm:h-12 sm:w-12"
						>
							<span class="fa-light fa-copy"></span>
						</div>
					</div>
				</template>
			</filter-bar>

			<filter-section
				:show="showFilters"
				:filters="searchParams"
				:search-term="searchField"
				:tenants="tenants"
				:accounts="accounts"
				date-label="Date Added"
				show-tenant-filter
				show-account-filter
				show-date-filter
				show-search-filter
				search-label="Name"
				@update:filters="searchParams = $event"
				@update:search-term="searchField = $event"
				@tenant-change="$global.setSelectedTenant"
			>
			</filter-section>

			<div class="mt-4 flex items-center justify-center pb-4 md:pb-0">
				<div
					class="relative flex w-full flex-wrap gap-2 self-center rounded-lg p-0.5 md:w-[800px] md:flex-nowrap"
				>
					<button
						v-for="option in displayOptions"
						:key="option.value"
						type="button"
						class="relative w-[calc(50%-0.25rem)] cursor-default whitespace-nowrap rounded-full border border-main px-4 py-2 text-sm font-medium transition-all active:scale-[0.98] dark:border-main md:w-1/2"
						:class="{
							'border-transparent bg-main text-white dark:border-transparent dark:bg-accentMain dark:text-black':
								display === option.value,
							'bg-transparent text-black hover:bg-main/10 dark:text-light dark:hover:bg-offwhite/10':
								display !== option.value
						}"
						@click="setDisplay(option.value)"
					>
						<span class="hidden sm:block">{{ option.label }}</span>
						<span class="block sm:hidden">{{ option.shortLabel || option.label }}</span>
					</button>
				</div>
			</div>

			<div v-if="display === 'flows'">
				<data-view
					:view-mode="'table'"
					:columns="columns"
					:sort-column="sortColumn"
					:reverse="reverse"
					:loading="loading"
					:label="'Flows'"
					:total-items="totalFlows"
					:page-size="pageSize"
					:current-page="currentPage"
					:pages="pages"
					@sort="setSort"
					@update:page-size="changePageSize"
					@page-change="changePage"
				>
					<template #table-view>
						<generic-table
							:items="flows"
							:columns="columns"
							item-type="flows"
							base-route="flows"
							:can-edit="$global.hasUserRole(userRoles, 'flows:update')"
							:view-mode="'table'"
							:empty-state-config="{
								icon: 'fa-kit fa-teleflow',
								title: 'No flows yet',
								description: 'Create your first call flow or use a template to get started.',
								action: $global.hasUserRole(userRoles, 'flows:create')
									? {
											label: 'Create Flow',
											icon: 'fa-light fa-plus',
											handler: () => routeTo('/flows/new')
										}
									: null
							}"
							:actions="[
								{
									handler: (flow) => routeTo(`/flows/${flow.id}`),
									icon: 'fa-light fa-pencil',
									label: 'Edit'
								},
								{
									permission: 'flows:create',
									handler: duplicateFlow,
									icon: 'fa-light fa-copy',
									label: 'Duplicate'
								},
								{
									permission: 'flows:create',
									handler: convertToTemplate,
									icon: 'fa-light fa-wand-magic-sparkles',
									label: 'Convert to Template'
								},
								{
									permission: 'flows:delete',
									handler: deleteFlow,
									icon: 'fa-light fa-delete-left',
									label: 'Delete'
								}
							]"
						>
							<template #tenant="{ item }">
								{{ item.tenant?.companyName || 'Unassigned' }}
							</template>
							<template #account="{ item }">
								{{ item.account?.name || 'Unassigned' }}
							</template>
							<template #mobile-view="{ item }">
								<div class="flex w-full flex-col">
									<div class="text-md absolute right-4 top-4 block max-w-[160px]">
										<p class="text-right font-semibold uppercase text-gray-400">Account</p>
										<p class="truncate text-right">{{ item.account?.name || 'Unassigned' }}</p>
									</div>

									<div class="flex flex-col gap-2">
										<div class="flex flex-col">
											<p class="font-semibold uppercase text-gray-500">Name:</p>
											<p>{{ item.name }}</p>
										</div>
										<div class="flex flex-col">
											<p class="font-semibold uppercase text-gray-500">Tenant:</p>
											<p>{{ item.tenant?.companyName || 'Unassigned' }}</p>
										</div>
										<div class="flex flex-col">
											<p class="font-semibold uppercase text-gray-500">Created:</p>
											<p>{{ $global.dateFormat(item.createdAt) }}</p>
										</div>
									</div>
								</div>
							</template>
						</generic-table>
					</template>
				</data-view>
			</div>

			<div v-if="display === 'templates'">
				<div class="flex items-center justify-between">
					<div class="flex items-center">
						<p class="textUppercase textSml whitespace-nowrap">
							Flow Templates <span class="fontHeavy textGreen textSml"> {{ flowTemplates.length }}</span>
						</p>
					</div>
				</div>

				<div class="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
					<div v-if="flowTemplates.length === 0" class="col-span-full py-8 text-center">
						<div
							class="mx-auto flex max-w-md flex-col items-center justify-center rounded-lg border border-dashed border-light-600 p-6 dark:border-offmain"
						>
							<span
								class="fa-light fa-file-template mb-4 text-4xl text-light-700 dark:text-light-600"
							></span>
							<h3 class="mb-2 text-lg font-medium text-main dark:text-white">No templates found</h3>
							<p class="mb-4 text-sm text-mainLight dark:text-light-600">
								You haven't created any flow templates yet. Convert an existing flow to a template to
								get started.
							</p>
							<button
								v-if="$global.hasUserRole(userRoles, 'flows:create')"
								class="jmiBtn blue"
								@click="showFlowSelectionModal = true"
							>
								Convert Flow to Template
							</button>
						</div>
					</div>
					<div
						v-for="template in flowTemplates"
						:key="template.id"
						class="cursor-pointer rounded-lg border border-light-600 bg-white p-5 shadow-sm transition-all hover:border-blue-500 hover:shadow-md dark:border-offmain dark:bg-offmaindark"
						@click="useTemplate(template)"
					>
						<div class="flex h-full flex-col gap-3">
							<div class="flex items-center justify-between">
								<h3 class="truncate text-lg font-medium">{{ template.name }}</h3>
								<div class="flex gap-1">
									<button
										v-if="$global.hasUserRole(userRoles, 'flows:update')"
										class="rounded p-1 text-light-700 hover:bg-light-100 hover:text-blue-500 dark:text-light-600 dark:hover:bg-offmain"
										@click.stop="editTemplate(template)"
									>
										<span class="fa-light fa-pencil"></span>
									</button>
									<button
										v-if="$global.hasUserRole(userRoles, 'flows:delete')"
										class="rounded p-1 text-light-700 hover:bg-light-100 hover:text-red-500 dark:text-light-600 dark:hover:bg-offmain"
										@click.stop="deleteTemplate(template)"
									>
										<span class="fa-light fa-delete-left"></span>
									</button>
								</div>
							</div>
							<p class="line-clamp-3 flex-grow text-sm text-main dark:text-light-600">
								{{ template.description || 'No description available' }}
							</p>
							<div class="mt-auto flex items-center justify-between">
								<span class="text-xs text-mainLight dark:text-light-700">
									{{ $global.dateFormat(template.createdAt) }}
								</span>
								<button
									class="rounded-full bg-blue-500 px-3 py-1 text-xs text-white hover:bg-blue-600"
									@click.stop="useTemplate(template)"
								>
									Use
								</button>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>

		<UseFlowTemplateModal
			:is-open="showTemplateModal"
			:template="selectedTemplate"
			:tenants="tenants"
			:accounts="accounts"
			@update:is-open="showTemplateModal = $event"
			@flow-created="handleFlowCreated"
		/>

		<ConvertToTemplateModal
			:is-open="showConvertModal"
			:flow-id="convertToTemplateId"
			@update:is-open="showConvertModal = $event"
			@template-created="handleTemplateCreated"
			@reload-flows="reloadFlows"
		/>

		<Dialog :open="showTemplateSelectionModal" @update:open="showTemplateSelectionModal = $event">
			<DialogContent class="max-w-3xl" :disable-overlay-close="true" @close="showTemplateSelectionModal = false">
				<DialogHeader>
					<DialogTitle>Select a Template</DialogTitle>
					<DialogDescription>Choose a template to create a new flow</DialogDescription>
				</DialogHeader>

				<div class="my-4 grid max-h-[80vh] grid-cols-1 gap-4 overflow-y-auto sm:grid-cols-2 md:grid-cols-3">
					<div v-if="flowTemplates.length === 0">
						<p class="text-gray-500">No templates found</p>
					</div>
					<div
						v-for="template in flowTemplates"
						:key="template.id"
						class="relative cursor-pointer rounded-md border border-gray-200 p-4 transition-all hover:border-blue-500 hover:shadow-md dark:border-gray-700 dark:hover:border-blue-400"
						@click="selectTemplate(template)"
					>
						<div class="pr-2">
							<h3 class="mb-2 truncate text-lg font-medium dark:text-white">{{ template.name }}</h3>
							<p class="mb-2 line-clamp-3 text-sm text-gray-500 dark:text-gray-400">
								{{ template.description || 'No description available' }}
							</p>
						</div>
					</div>
				</div>

				<DialogFooter>
					<button class="jmiBtn red" @click="showTemplateSelectionModal = false">Cancel</button>
				</DialogFooter>
			</DialogContent>
		</Dialog>

		<Dialog :open="showFlowSelectionModal" @update:open="showFlowSelectionModal = $event">
			<DialogContent class="max-w-4xl" :disable-overlay-close="true" @close="showFlowSelectionModal = false">
				<DialogHeader>
					<DialogTitle>Select a Flow to Convert</DialogTitle>
					<DialogDescription>Choose a flow to convert into a template</DialogDescription>
				</DialogHeader>

				<div class="my-4 grid max-h-[80vh] grid-cols-1 gap-4 overflow-y-auto sm:grid-cols-2 md:grid-cols-3">
					<div v-if="flows.length === 0">
						<p class="text-gray-500">No flows found to convert</p>
					</div>
					<div
						v-for="flow in flows"
						:key="flow.id"
						class="relative cursor-pointer rounded-md border border-gray-200 p-4 transition-all hover:border-blue-500 hover:shadow-md dark:border-gray-700 dark:hover:border-blue-400"
						@click="convertFlowToTemplate(flow.id)"
					>
						<div class="pr-2">
							<h3 class="mb-2 truncate text-lg font-medium dark:text-white">{{ flow.name }}</h3>
							<p class="mb-2 text-sm text-gray-500 dark:text-gray-400">
								{{ flow.tenant?.companyName || 'Unassigned' }} /
								{{ flow.account?.name || 'Unassigned' }}
							</p>
							<p class="text-xs text-gray-400">Created: {{ $global.dateFormat(flow.createdAt) }}</p>
						</div>
					</div>
				</div>

				<DialogFooter>
					<button class="jmiBtn red" @click="showFlowSelectionModal = false">Cancel</button>
				</DialogFooter>
			</DialogContent>
		</Dialog>

		<EditTemplateModal
			:is-open="showEditTemplateModal"
			:template="selectedTemplateForEdit"
			@update:is-open="showEditTemplateModal = $event"
			@template-updated="handleTemplateUpdated"
			@template-deleted="handleTemplateDeleted"
		/>

		<EditTemplateModal
			:is-open="showCreateTemplateModal"
			:template="{}"
			:is-new="true"
			@update:is-open="showCreateTemplateModal = $event"
			@template-created="handleTemplateCreated"
		/>
	</div>
</template>

<script>
import flowsApi from '../scripts/flows/flows.api'
import flowTemplatesApi from '../scripts/flowtemplates/flowtemplates.api'
import accountsApi from '../scripts/accounts/accounts.api'
import { router } from '../router'
import moment from 'moment'
import debounce from 'lodash.debounce'
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
	DialogFooter
} from '../components/ui/dialog'
import UseFlowTemplateModal from '../components/UseFlowTemplateModal.vue'
import ConvertToTemplateModal from '../components/ConvertToTemplateModal.vue'
import EditTemplateModal from '../components/EditTemplateModal.vue'
export default {
	components: {
		Dialog,
		DialogContent,
		DialogHeader,
		DialogTitle,
		DialogDescription,
		DialogFooter,
		UseFlowTemplateModal,
		ConvertToTemplateModal,
		EditTemplateModal
	},
	inject: ['user', 'userRoles', 'tenants'],
	data() {
		return {
			columns: [
				{
					key: 'name',
					label: 'Name',
					sortable: true,
					class: 'col-xs-12 col-sm-3 col-md-3 col-lg-3'
				},
				{
					key: 'tenant',
					label: 'Tenant',
					class: 'col-lg-2 col-md-3 col-sm-3'
				},
				{
					key: 'account',
					label: 'Account',
					class: 'col-lg-2 col-md-3 col-sm-3'
				},
				{
					key: 'createdAt',
					label: 'Date Added',
					sortable: true,
					class: 'col-xs-3 col-lg-2'
				}
			],
			showFilters: true,
			searchParams: {
				dateAddedFrom: null,
				dateAddedTo: null,
				selectedTenant: this.$global.getSelectedTenant() || '',
				selectedAccount: ''
			},
			flows: [],
			accounts: [],
			totalFlows: 0,
			pageSize: 25,
			currentPage: 1,
			sortColumn: 'name',
			reverse: false,
			loading: false,
			searchField: '',
			showTemplateModal: false,
			showTemplateSelectionModal: false,
			showConvertModal: false,
			showEditTemplateModal: false,
			showCreateTemplateModal: false,
			showFlowSelectionModal: false,
			convertToTemplateId: null,
			selectedTemplate: null,
			selectedTemplateForEdit: null,
			flowTemplates: [],
			display: 'flows',
			displayOptions: [
				{ value: 'flows', label: 'Flows', shortLabel: 'Flows' },
				{ value: 'templates', label: 'Flow Templates', shortLabel: 'Templates' }
			]
		}
	},

	computed: {
		pages() {
			const numPages = Math.ceil(this.totalFlows / this.pageSize) || 1
			return Array.from({ length: numPages }, (_, i) => i)
		},
		selectedTenantName() {
			if (!this.searchParams.selectedTenant) return 'All Tenants'
			const tenant = this.tenants?.find((t) => t.id === parseInt(this.searchParams.selectedTenant))
			return tenant?.companyName || 'All Tenants'
		}
	},

	watch: {
		searchParams: {
			handler() {
				this.reloadFlows()
			},
			deep: true,
			immediate: false
		},
		searchField(newValue, oldValue) {
			this.debouncedWatch(newValue, oldValue)
		},
		'$route.query': {
			handler(newQuery) {
				const tab = newQuery.tab
				if (tab && ['flows', 'templates'].includes(tab)) {
					this.display = tab
				}
			},
			deep: true,
			immediate: true
		}
	},

	created() {
		accountsApi
			.getAll()
			.then((response) => {
				this.accounts = response.data.results
			})
			.catch(console.error)

		this.loadTemplates()

		this.debouncedWatch = debounce(this.reloadFlows, 1000)
		this.reloadFlows(this.$route.query?.globalSearch)
	},

	mounted() {
		const tab = this.$route.query.tab
		if (tab && ['flows', 'templates'].includes(tab)) {
			this.display = tab
		}
	},

	beforeUnmount() {
		this.debouncedWatch.cancel()
		if (this.getRefreshInterval) clearInterval(this.getRefreshInterval)
	},

	methods: {
		setDisplay(tab) {
			this.display = tab
			this.$router.push({ query: { ...this.$route.query, tab } })
		},

		loadTemplates() {
			flowTemplatesApi
				.getAll()
				.then((response) => {
					this.flowTemplates = response.data.results
				})
				.catch((error) => {
					console.error('Error loading templates:', error)
				})
		},

		resetSearchParams() {
			this.searchParams = {
				dateAddedFrom: null,
				dateAddedTo: null,
				selectedTenant: '',
				selectedAccount: ''
			}
		},

		setSort(column, reverse) {
			if (this.sortColumn !== column) {
				this.reverse = false
			} else {
				this.reverse = reverse
			}
			this.sortColumn = column
			this.reloadFlows()
		},

		setTemplateSort(column, reverse) {
			this.flowTemplates.sort((a, b) => {
				const aValue = a[column]
				const bValue = b[column]

				if (typeof aValue === 'string' && typeof bValue === 'string') {
					return reverse ? bValue.localeCompare(aValue) : aValue.localeCompare(bValue)
				}

				return reverse ? bValue - aValue : aValue - bValue
			})
		},

		getSearchParams() {
			const params = {
				limit: this.pageSize,
				skip: this.pageSize * (this.currentPage - 1),
				where: {},
				order: {
					[this.sortColumn || 'createdAt']: this.reverse ? 'DESC' : 'ASC'
				}
			}

			if (this.searchParams) {
				const { dateAddedFrom, dateAddedTo, selectedTenant, selectedAccount } = this.searchParams

				if (dateAddedFrom) {
					params.where.createdAtFrom = `${moment(dateAddedFrom).format('YYYY-MM-DD')} 00:00:00`
					params.where.createdAtTo = dateAddedTo
						? `${moment(dateAddedTo).format('YYYY-MM-DD')} 23:59:59`
						: `${moment(dateAddedFrom).format('YYYY-MM-DD')} 23:59:59`
				}

				if (selectedTenant) params.where.selectedTenant = selectedTenant
				if (selectedAccount) params.where.selectedAccount = selectedAccount
			}

			return params
		},

		async reloadFlows(globalSearch) {
			try {
				this.loading = true
				let params = this.getSearchParams()

				if (globalSearch) {
					params = { ...params, searchTerm: globalSearch }
				} else if (this.searchField) {
					params.searchTerm = this.searchField
				}

				const response = await flowsApi.getAll(params)
				this.totalFlows = response.data.count
				this.flows = response.data.results

				const maxPages = Math.ceil(this.totalFlows / this.pageSize) || 1
				if (this.currentPage > maxPages) {
					this.currentPage = 1
					this.reloadFlows(globalSearch)
					return
				}
			} catch (err) {
				console.error(err)
			} finally {
				this.loading = false
			}
		},

		routeTo(routeName) {
			router.push(routeName)
		},

		toggleViewMode() {
			this.viewMode = this.viewMode === 'card' ? 'table' : 'card'
		},

		changePage(page) {
			this.currentPage = page
			this.reloadFlows()
		},

		changePageSize(size) {
			this.pageSize = size
			const numPages = Math.ceil(this.totalFlows / size) || 1
			this.currentPage = Math.min(this.currentPage, numPages)
			this.reloadFlows()
		},

		save() {
			this.reloadFlows()
		},

		async duplicateFlow(flow) {
			try {
				const copy = { ...flow }
				delete copy.id
				copy.name = flow.name + ' (Copy)'
				await flowsApi.create(copy)
				this.reloadFlows()
			} catch (err) {
				console.error(err)
			}
		},

		deleteFlow(flow) {
			this.$swal({
				title: 'Delete Flow',
				text: `Would you like to delete flow '${flow.name}'?`,
				showCancelButton: true,
				confirmButtonText: 'Delete',
				cancelButtonText: 'Cancel'
			}).then((result) => {
				if (result.isConfirmed) {
					flowsApi
						.delete(flow.id)
						.then(() => this.reloadFlows())
						.catch(console.error)
				}
			})
		},

		deleteTemplate(template) {
			this.$swal({
				title: 'Delete Template',
				text: `Would you like to delete template '${template.name}'?`,
				showCancelButton: true,
				confirmButtonText: 'Delete',
				cancelButtonText: 'Cancel'
			}).then((result) => {
				if (result.isConfirmed) {
					flowTemplatesApi
						.delete(template.id)
						.then(() => {
							this.handleTemplateDeleted(template.id)
						})
						.catch(console.error)
				}
			})
		},

		selectTemplate(template) {
			this.selectedTemplate = template
			this.showTemplateSelectionModal = false
			this.showTemplateModal = true
		},

		useTemplate(template) {
			this.selectedTemplate = template
			this.showTemplateModal = true
		},

		editTemplate(template) {
			this.selectedTemplateForEdit = template
			this.showEditTemplateModal = true
		},

		convertFlowToTemplate(flowId) {
			this.convertToTemplateId = flowId
			this.showFlowSelectionModal = false
			this.showConvertModal = true
		},

		handleTemplateUpdated(updatedTemplate) {
			const index = this.flowTemplates.findIndex((t) => t.id === updatedTemplate.id)
			if (index !== -1) {
				const existingTemplate = this.flowTemplates[index]
				const mergedTemplate = {
					...existingTemplate,
					name: updatedTemplate.name,
					description: updatedTemplate.description
				}
				this.flowTemplates.splice(index, 1, mergedTemplate)
			}

			this.$swal({
				title: 'Success',
				text: 'Template updated successfully',
				icon: 'success',
				confirmButtonText: 'OK'
			})
		},

		handleTemplateDeleted(templateId) {
			this.flowTemplates = this.flowTemplates.filter((t) => t.id !== templateId)

			this.$swal({
				title: 'Success',
				text: 'Template deleted successfully',
				icon: 'success',
				confirmButtonText: 'OK'
			})
		},

		async handleFlowCreated(flow) {
			await flowsApi.create(flow).then(() => this.reloadFlows())
			this.selectedTemplate = null
			console.log('Flow created:', flow)
			this.$swal({
				title: 'Success',
				text: `Flow '${flow.name}' created successfully`,
				icon: 'success',
				confirmButtonText: 'OK'
			})
		},

		convertToTemplate(flow) {
			console.log('Converting flow to template:', flow)
			this.convertToTemplateId = flow.id
			this.showConvertModal = true
		},

		handleTemplateCreated(template) {
			console.log('Template created:', template)
			this.flowTemplates.push(template)
			this.setTemplateSort('name', false)

			this.$swal({
				title: 'Success',
				text: `Template "${template.name}" created successfully!`,
				icon: 'success',
				confirmButtonText: 'OK'
			})
		}
	}
}
</script>
